import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { DealerType } from '@1po/1po-bff-fe-spec/generated/user/model/CommercialLink';
import { TFunction } from 'i18next';
import {
  ROUTER_ACCOUNT,
  ROUTER_ASSISTANCE,
  ROUTER_ESTIMATE,
  ROUTER_ORDER_LIST,
  ROUTER_ORDERS,
  ROUTER_PROMOTION,
  ROUTER_TIRES,
  ROUTER_UNIVERSAL_PRODUCTS,
} from 'app/AppRouter';
import { RenaultGroupFooterIcon, TireMudIcon } from 'assets/icons';
import { LinksGrid, SContainer, SText } from 'components/Page/SubFooter/SubFooter.styled';
import { SSOLink } from 'components/SSOLink/SSOLink';
import { getReuseLink } from 'domains/appContext/AppContext.store';
import { getCurrentPromotionsData } from 'domains/promotion/Promotion.store';
import { CLARION, IDIR } from 'domains/ssoLink/SSOLink.types';
import { getDealerType, getUserCountry, UserRole } from 'domains/user';
import { CREATE_NEW } from 'pages/BackOfficePage/BackOfficePage.type';
import { theme } from 'styles';
import { Box, Flex, Icon, Link, MarginBox, Text } from 'UI';
import { getCondArrayItem, useBreakpointSelectorFull, useLarge } from 'utils';
import { Country } from 'utils/i18n/Country';

const ROUTER_EXTERNAL_E_GUIDE = 'https://www.e-guide.renault.com/portail/';
const ROUTER_EXTERNAL_USER_GUIDE = 'https://www.userguide-renaultgroup.com';
const ROUTER_EXTERNAL_SECONDE_VIE_AUTOMOBILE = 'https://sva2.dcs2.renault.com';

type LinkItem = { link: string; text: string; external?: boolean } | { component: ReactNode };
type LinkItemsColumn = { id: string; title: string; linkItems: LinkItem[] };

const getLinkItemsColumns = (
  t: TFunction,
  reuseUrl: string | undefined,
  showCurrentPromotions: boolean,
  searchParams: URLSearchParams,
  country?: string,
  dealerType?: DealerType,
): LinkItemsColumn[] => {
  const showTires = Country.canShowTiresByCountry(country);
  const allowedDealerTypes: DealerType[] = [UserRole.R1, UserRole.R2, UserRole.R3_MOTRIO];
  return [
    {
      id: '1',
      title: t('page.subfooter.link_title.get_in_touch', 'Get in touch with us'),
      linkItems: [
        {
          link: ROUTER_ASSISTANCE,
          text: t('assistance', 'Assistance'),
        },
      ],
    },
    {
      id: '2',
      title: t('page.subfooter.link_title.solutions', 'Solutions'),
      linkItems: [
        {
          link: `${ROUTER_ORDER_LIST}/${CREATE_NEW}`,
          text: t('catalog.order_by_reference', 'Order by reference'),
        },
        {
          link: `${ROUTER_ESTIMATE}?${searchParams}`,
          text: t('footer.estimates', 'Estimates'),
        },
        {
          link: ROUTER_ORDERS,
          text: t('footer.my_orders', 'My orders'),
        },
      ],
    },
    {
      id: '3',
      title: t('page.subfooter.link_title.direct_links', 'Direct links'),
      linkItems: [
        ...getCondArrayItem(
          showCurrentPromotions && {
            link: ROUTER_PROMOTION,
            text: t('promotions.current_promotions', 'Current promotions'),
          },
        ),
        {
          link: ROUTER_ACCOUNT,
          text: t('profile.my_profile', 'My profile'),
        },
        ...getCondArrayItem(
          showTires && {
            link: ROUTER_TIRES,
            icon: TireMudIcon,
            text: t('catalog.tires', 'Tires'),
          },
        ),
        {
          link: ROUTER_UNIVERSAL_PRODUCTS,
          text: t('catalog.universal_products', 'Universal products'),
        },
      ],
    },
    {
      id: '4',
      title: t('page.subfooter.link_title.other_sites', 'Other sites'),
      linkItems: [
        {
          link: ROUTER_EXTERNAL_E_GUIDE,
          text: t('footer.e_guide', 'e-Guide'),
          external: true,
        },
        {
          link: ROUTER_EXTERNAL_USER_GUIDE,
          text: t('footer.user_guide', 'User guide'),
          external: true,
        },
        ...getCondArrayItem(
          country === Country.FR.key && {
            link: ROUTER_EXTERNAL_SECONDE_VIE_AUTOMOBILE,
            text: t('footer.seconde_vie_automobile', 'Seconde Vie Automobile'),
            external: true,
          },
        ),
        ...getCondArrayItem(
          country === Country.FR.key &&
            dealerType &&
            allowedDealerTypes.includes(dealerType) &&
            reuseUrl !== undefined && {
              link: reuseUrl,
              text: t('footer.re_use', 'ReUse'),
              external: true,
            },
        ),
        {
          component: country === Country.IT.key && (
            <SSOLink
              ssoType={IDIR}
              renderLinkComponent={() => (
                <SText type={'dark_14_white_65'} noWrap cursor={'pointer'}>
                  {t('footer.idir', 'IDIR')}
                </SText>
              )}
              renderLoadingComponent={() => (
                <SText type={'dark_14_white_65'} noWrap>
                  {t('link.redirecting_in_progress', 'Redirecting...')}
                </SText>
              )}
            />
          ),
        },
        {
          component: (
            <SSOLink
              ssoType={CLARION}
              renderLinkComponent={() => (
                <SText type={'dark_14_white_65'} noWrap cursor={'pointer'}>
                  {t('footer.clarion-electronic_repair', 'Clarion - Electronic repair')}
                </SText>
              )}
              renderLoadingComponent={() => (
                <SText type={'dark_14_white_65'} noWrap>
                  {t('link.redirecting_in_progress', 'Redirecting...')}
                </SText>
              )}
            />
          ),
        },
      ],
    },
  ];
};

export const SubFooter = () => {
  const { t } = useTranslation();
  const promotionsData = useSelector(getCurrentPromotionsData);
  const showCurrentPromotions = promotionsData?.length > 0;
  const dealerType = useSelector(getDealerType);
  const country = useSelector(getUserCountry);
  const searchParams = new URLSearchParams(location.search);
  const reuseUrl = useSelector(getReuseLink);
  const isLarge = useLarge();
  const gridColumns = useBreakpointSelectorFull()({ xs: 1, sm: 1, md: 2, lg: 2, xl: 4, xml: 4, xxl: 4 }) ?? 1;

  const linkColumns = getLinkItemsColumns(t, reuseUrl, showCurrentPromotions, searchParams, country, dealerType);

  const renderLinkColumn = (linkItemsColumn: LinkItemsColumn) => (
    <Flex direction={'column'} key={linkItemsColumn.id}>
      <MarginBox mb={18}>
        <Text type={'lead_text_white'} noWrap>
          {linkItemsColumn.title}
        </Text>
      </MarginBox>
      {linkItemsColumn.linkItems.map((linkItem, i) =>
        'link' in linkItem ? (
          <Box key={linkItem.link}>
            <Link to={linkItem.link} external={linkItem.external} newTab={linkItem.external}>
              <SText type={'dark_14_white_65'} noWrap>
                {linkItem.text}
              </SText>
            </Link>
          </Box>
        ) : (
          <Box key={`link_${i}`}>{linkItem.component}</Box>
        ),
      )}
    </Flex>
  );

  return (
    <SContainer>
      {isLarge ? (
        <Flex direction={'row'} justify={'space-around'} gap={32}>
          <Flex />
          <Flex>
            <Icon
              IconComponent={RenaultGroupFooterIcon}
              width={145}
              height={65}
              mt={5}
              color={theme.color.white}
              display={'block'}
            />
          </Flex>
          {linkColumns.map(renderLinkColumn)}
          <Flex />
        </Flex>
      ) : (
        <Flex direction={'column'} align={'center'} gap={32}>
          <Icon
            IconComponent={RenaultGroupFooterIcon}
            width={145}
            height={65}
            mt={5}
            color={theme.color.white}
            display={'block'}
          />
          <LinksGrid $columns={gridColumns}>{linkColumns.map(renderLinkColumn)}</LinksGrid>
        </Flex>
      )}
    </SContainer>
  );
};
